import React from 'react';
import {
  type ComponentSize,
  type ComponentVariant,
  type ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import Loading from 'src/modules/shared/components/atoms/loading';
import {
  baseButtonClasses,
  buttonFilledClasses,
  buttonGhostClasses,
  buttonOutlineClasses,
  buttonSoftClasses,
} from './button.styles';
import {
  controlTriggerPaddingClasses,
  controlTriggerSizeClasses,
} from 'src/modules/shared/styles/component.styles';
import type { ButtonProps } from 'src/modules/shared/types/button.types';

const loadingSizeClasses: Record<NonNullable<ComponentSize>, string> = {
  '2xs': 'w-1 h-1',
  xs: 'w-2 h-2',
  sm: 'w-2.5 h-2.5',
  md: 'w-3 h-3',
  lg: 'w-3.5 h-3.5',
};

const appearanceClasses: Record<
  NonNullable<ComponentAppearance>,
  Record<NonNullable<ComponentVariant>, string>
> = {
  filled: buttonFilledClasses,
  outline: buttonOutlineClasses,
  soft: buttonSoftClasses,
  ghost: buttonGhostClasses,
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className = '',
      variant = 'primary',
      appearance = 'filled',
      size = 'md',
      disabled = false,
      loading = false,
      isIconOnly = false,
      shape = 'default',
      ...rest
    },
    ref
  ) => {
    const classes = [
      ...baseButtonClasses,
      shape === 'round' ? 'rounded-full' : 'rounded-md',
      appearanceClasses[appearance][variant],
      controlTriggerSizeClasses[size],
      !isIconOnly && controlTriggerPaddingClasses[size],
      isIconOnly && 'aspect-square',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const getLoadingVariant = (
      currentVariant: ComponentVariant
    ): ComponentVariant => {
      if (currentVariant === 'secondary' || currentVariant === 'neutral') {
        return 'primary';
      }

      return 'secondary';
    };

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <>
            {children}
            <Loading
              className={`w-fit ${loadingSizeClasses[size]}`}
              iconClassName={loadingSizeClasses[size]}
              variant={getLoadingVariant(variant)}
              appearance={appearance}
            />
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
